import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../api/Api.js";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";
import "./VisaAssistanceManage.css";

const HolidayPackagesManage = () => {
  const navigate = useNavigate();

  // Website URL
  const urlLink = "https://www.patratravels.com/holidays/";

  // ==============================
  // STATES
  // ==============================
  const [getHolidaysdata, setHolidaysData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const holidaysPerPage = 10;

  // ==============================
  // INITIAL LOAD
  // ==============================
  useEffect(() => {
    fetchHolidaysData();
  }, []);

  // ==============================
  // FETCH HOLIDAYS DATA
  // ==============================
  const fetchHolidaysData = async () => {
    try {
      const res = await api.get(
        "/adminHolidays/get-holidays-package-data"
      );

      const data = res.data?.data || [];

      setHolidaysData(data);
    } catch (error) {
      console.error("Fetch Holidays Error:", error);
      toast.error("Failed to load Holidays Package data");
    }
  };

  // ==============================
  // VIEW DETAILS
  // ==============================
const handleViewDetails = (holiday) => {
  const PATRATARAVELS_URL = process.env.REACT_APP_PATRATARAVELS_URL;

  const baseUrl = `${PATRATARAVELS_URL}/holidays/`;

  const packageType = holiday.package_id_master;
  const packageSlug = holiday.holdy_pck_link;

  const url = `${baseUrl}${packageType}/${packageSlug}`;

  window.open(url, "_blank", "noopener,noreferrer");
};

  // ==============================
  // EDIT HOLIDAY
  // ==============================
  const handleEditPage = (pageID) => {
    navigate(`/holidays/holiday-packages-edit/${pageID}`);
  };

  // ==============================
  // DELETE HOLIDAY
  // ==============================
  const handleDeleteHolidays = async (pageID) => {
  const confirm = await Swal.fire({
    title: "Delete Holidays Package?",
    text: "Do you want to delete this Holidays Package?",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes Delete",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#dc3545",
    cancelButtonColor: "#6c757d",
  });

  if (!confirm.isConfirmed) {
    return;
  }

  try {
    const res = await api.delete(
      `/adminHolidays/holidays-package-delete/${pageID}`
    );

    if (res.data?.success) {
      await Swal.fire({
        title: "Deleted!",
        text: "Holidays Package deleted successfully",
        icon: "success",
        confirmButtonText: "OK",
      });

      // Reload holidays package data
      await fetchHolidaysData();

      // Adjust pagination after deletion
      const remainingData = getHolidaysdata.length - 1;
      const newTotalPages = Math.ceil(
        remainingData / holidaysPerPage
      );

      if (newTotalPages === 0) {
        setCurrentPage(1);
      } else if (currentPage > newTotalPages) {
        setCurrentPage(newTotalPages);
      }
    } else {
      toast.error(
        res.data?.message || "Failed to delete Holidays Package"
      );
    }
  } catch (error) {
    console.error("Delete Holiday Error:", error);

    toast.error(
      error.response?.data?.message ||
        "Failed to delete Holidays Package"
    );
  }
};

  // ==============================
  // STATUS CHANGE
  // ==============================
  const handleStatusChange = async (package_id, is_active) => {
    try {
      const status = Number(is_active);

      await api.put("/adminHolidays/holidays-package-status", {
        package_id,
        is_active: status,
      });

      toast.success("Status updated successfully");

      await fetchHolidaysData();
    } catch (error) {
      console.error("Status Update Error:", error);

      toast.error("Failed to update status");

      // Revert / reload data
      fetchHolidaysData();
    }
  };

  // ==============================
  // SORT ORDER UPDATE
  // ==============================
  const updateSortOrder = async (id, sort_order) => {
    try {
      const sortValue = Number(sort_order);

      if (sort_order === "" || Number.isNaN(sortValue)) {
        toast.error("Please enter a valid sort order");
        return;
      }

      await api.put(
        "/adminHolidays/update-holidays-sort-order",
        {
          id,
          sort_order: sortValue,
        }
      );

      toast.success("Sort order updated");

      await fetchHolidaysData();
    } catch (error) {
      console.error("Sort Order Update Error:", error);

      toast.error("Failed to update sort order");
    }
  };

  // ==============================
  // PAGINATION
  // ==============================
  const indexOfLastHolidays =
    currentPage * holidaysPerPage;

  const indexOfFirstHolidays =
    indexOfLastHolidays - holidaysPerPage;

  const currentHolidays = getHolidaysdata.slice(
    indexOfFirstHolidays,
    indexOfLastHolidays
  );

  const totalPages = Math.ceil(
    getHolidaysdata.length / holidaysPerPage
  );

  // ==============================
  // CHANGE PAGE
  // ==============================
  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= totalPages
    ) {
      setCurrentPage(pageNumber);
    }
  };

  // ==============================
  // FORMAT COUNTRY
  // ==============================
  const formatCountry = (country) => {
    if (!country) {
      return "-";
    }

    return country
      .replace(/-/g, " ")
      .replace(/\b\w/g, (char) =>
        char.toUpperCase()
      );
  };

  // ==============================
  // FORMAT DATE
  // ==============================
  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(/\//g, "-")
      .replace(",", "");
  };

  // ==============================
  // RENDER
  // ==============================
  return (
    <div className="container-fluid py-3 bg-white min-vh-100">

      {/* =========================================
          HEADER
      ========================================= */}
      <div className="card shadow-sm border-0 mb-4 bg-primary-subtle rounded-3">
        <div className="card-body d-flex justify-content-between align-items-center">

          <h4 className="fw-bold mb-0">
            Holiday Packages Manage
          </h4>

          <Link
            to="/holidays/holiday-packages-add"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          >
            <ArrowLeft size={16} />
            Add
          </Link>

        </div>
      </div>

      {/* =========================================
          TABLE
      ========================================= */}
      <div className="card shadow-sm border-0 rounded-3 overflow-hidden">

        <div className="table-responsive">

          <table
            className="table table-hover table-sm align-middle mb-0"
            style={{
              tableLayout: "fixed",
              width: "100%",
            }}
          >

            {/* =====================================
                TABLE HEADER
            ===================================== */}
            <thead className="bg-light">

              <tr>

                <th
                  className="text-center"
                  style={{ width: "5%" }}
                >
                  Sl. No.
                </th>

                <th
                  className="text-center"
                  style={{ width: "8%" }}
                >
                  Sort
                </th>

                <th
                  className="text-center"
                  style={{ width: "20%" }}
                >
                  Package Name
                </th>

                <th
                  className="text-center"
                  style={{ width: "20%" }}
                >
                  Title
                </th>

                <th
                  className="text-center"
                  style={{ width: "12%" }}
                >
                  Country
                </th>

                <th
                  className="text-center"
                  style={{ width: "8%" }}
                >
                  View
                </th>

                <th
                  className="text-center"
                  style={{ width: "12%" }}
                >
                  Date
                </th>

                <th
                  className="text-center"
                  style={{ width: "10%" }}
                >
                  Status
                </th>

                <th
                  className="text-center"
                  style={{ width: "10%" }}
                >
                  Action
                </th>

              </tr>

            </thead>

            {/* =====================================
                TABLE BODY
            ===================================== */}
            <tbody>

              {currentHolidays.length === 0 ? (

                <tr>

                  <td
                    colSpan="9"
                    className="text-center py-5 text-muted"
                  >
                    Data Not Found.
                  </td>

                </tr>

              ) : (

                currentHolidays.map(
                  (holiday, index) => (

                    <tr
                      key={
                        holiday.package_id  ||
                        index
                      }
                    >

                      {/* =========================
                          SL NO
                      ========================= */}
                      <td className="text-center">
                        {indexOfFirstHolidays +
                          index +
                          1}
                      </td>

                      {/* =========================
                          SORT ORDER
                      ========================= */}
                      <td className="text-center">

                        <input
                          type="number"
                          min="0"
                          className="form-control form-control-sm text-center"
                          defaultValue={
                            holiday.sort_order ?? ""
                          }
                          onBlur={(e) =>
                            updateSortOrder(
                              holiday.package_id,
                              e.target.value
                            )
                          }
                        />

                      </td>

                      {/* =========================
                          PACKAGE NAME
                      ========================= */}
                      <td
                        className="text-center"
                        style={{
                          wordBreak: "break-word",
                        }}
                      >
                        {holiday.package_id_master ||
                          "-"}
                      </td>

                      {/* =========================
                          TITLE
                      ========================= */}
                      <td
                        className="text-center"
                        style={{
                          wordBreak: "break-word",
                        }}
                      >
                        {holiday.title || "-"}
                      </td>

                      {/* =========================
                          COUNTRY
                      ========================= */}
                      <td className="text-center">

                        {holiday.country_id}

                      </td>

                      {/* =========================
                          WEBSITE LINK
                      ========================= */}
                     

                      {/* =========================
                          VIEW DETAILS
                      ========================= */}
<td className="text-center">
  {holiday.status == 1 ? (
    <button
      type="button"
      className="btn btn-sm btn-success"
      onClick={() => handleViewDetails(holiday)}
      title="View Details"
    >
      <i className="fa fa-eye" aria-hidden="true"></i>
    </button>
  ) : (
    <span className="text-danger">
      Inactive
    </span>
  )}
</td>

                      {/* =========================
                          DATE
                      ========================= */}
                      <td className="text-center">

                        {formatDate(
                          holiday.created_at
                        )}

                      </td>

                      {/* =========================
                          STATUS
                      ========================= */}
                      <td className="text-center">

                        <select
                          className={`form-select form-select-sm border-0 fw-bold ${
                            Number(
                              holiday.status
                            ) === 1
                              ? "text-success bg-success-subtle"
                              : "text-danger bg-danger-subtle"
                          }`}
                          style={{
                            width: "100px",
                            margin: "0 auto",
                            cursor: "pointer",
                          }}
                          value={
                            holiday.status ?? 0
                          }
                          onChange={(e) =>
                            handleStatusChange(
                              holiday.package_id,
                              e.target.value
                            )
                          }
                        >

                          <option value="1">
                            Active
                          </option>

                          <option value="0">
                            Inactive
                          </option>

                        </select>

                      </td>

                      {/* =========================
                          ACTION
                      ========================= */}
                      <td className="text-center">

                        {/* EDIT */}
                        <button
                          type="button"
                          className="btn btn-sm btn-primary me-2"
                          onClick={() =>
                            handleEditPage(
                              holiday.package_id
                            )
                          }
                          title="Edit"
                        >
                          <i
                            className="fa fa-pencil"
                            aria-hidden="true"
                          ></i>
                        </button>

                        {/* DELETE */}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            handleDeleteHolidays(
                              holiday.package_id
                            )
                          }
                          title="Delete"
                        >
                          <i
                            className="fa fa-trash"
                            aria-hidden="true"
                          ></i>
                        </button>

                      </td>

                    </tr>

                  )
                )

              )}

            </tbody>

          </table>

        </div>

        {/* =========================================
            PAGINATION
        ========================================= */}
        {totalPages > 1 && (

          <div className="d-flex justify-content-center mt-3 mb-3">

            <nav aria-label="Holiday pagination">

              <ul className="pagination mb-0">

                {/* PREVIOUS */}
                <li
                  className={`page-item ${
                    currentPage === 1
                      ? "disabled"
                      : ""
                  }`}
                >

                  <button
                    type="button"
                    className="page-link"
                    disabled={currentPage === 1}
                    onClick={() =>
                      handlePageChange(
                        currentPage - 1
                      )
                    }
                  >
                    Previous
                  </button>

                </li>

                {/* PAGE NUMBERS */}
                {Array.from(
                  { length: totalPages },
                  (_, i) => i + 1
                ).map((pageNumber) => (

                  <li
                    key={pageNumber}
                    className={`page-item ${
                      currentPage === pageNumber
                        ? "active"
                        : ""
                    }`}
                  >

                    <button
                      type="button"
                      className="page-link"
                      onClick={() =>
                        handlePageChange(
                          pageNumber
                        )
                      }
                    >
                      {pageNumber}
                    </button>

                  </li>

                ))}

                {/* NEXT */}
                <li
                  className={`page-item ${
                    currentPage === totalPages
                      ? "disabled"
                      : ""
                  }`}
                >

                  <button
                    type="button"
                    className="page-link"
                    disabled={
                      currentPage === totalPages
                    }
                    onClick={() =>
                      handlePageChange(
                        currentPage + 1
                      )
                    }
                  >
                    Next
                  </button>

                </li>

              </ul>

            </nav>

          </div>

        )}

      </div>

    </div>
  );
};

export default HolidayPackagesManage;